import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, Descriptions, Form, Input, InputNumber,
  Row, Select, Space, Switch, Table, Tag, Typography,
} from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'

import apiClient from '@/shared/api/client'
import {
  spareProcurementApi,
  type SpareGRNLineInput,
  type SpareGRNResult,
} from '@/features/maintenance/spareProcurementApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ProcurementStatusBadge from './components/ProcurementStatusBadge'

const { Text } = Typography

interface POLineRow {
  id: string
  item_id: string | null
  spare_id: string | null
  maintenance_work_order_id: string | null
  direct_charge_to_work_order: boolean
  quantity: number | null
  quantity_kg: number | null
  uom: string | null
  rate: number | null
  rate_per_kg: number | null
  received_kg: number | null
}

interface PORow {
  id: string
  po_no: string
  status: string
  supplier_id: string | null
  supplier_name?: string
  lines: POLineRow[]
}

interface LineDraft {
  po_line_id: string
  quantity_received: number
  quantity_accepted: number
  quantity_rejected: number
  rate_per_unit: number
  uom: string
  gst_pct: number
  spare_id: string | null
  maintenance_work_order_id: string | null
  direct_charge_to_work_order: boolean
}

/**
 * Phase 3.2-B — Spare GRN entry.
 *
 * Spec §5. Pick a PO → its lines auto-fetch → store officer enters
 * received qty (and optionally rejected qty). Rate, UOM, machine, WO,
 * department and direct-charge flag are inherited from the PO line.
 * Backend computes landed rate, posts stock + WAvg, updates the link.
 */
export default function SpareGRNEntryPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [poId, setPoId] = useState<string | undefined>()
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({})
  const [freightTotal, setFreightTotal] = useState<number>(0)
  const [remarks, setRemarks] = useState<string>('')
  const [result, setResult] = useState<SpareGRNResult | null>(null)

  // List POs (use existing procurement endpoint)
  const { data: pos } = useQuery<PORow[]>({
    queryKey: ['pos-for-grn', plantId],
    enabled: !!plantId,
    queryFn: () => apiClient.get('/purchase-orders', {
      params: { plant_id: plantId },
    }).then((r) => r.data),
  })
  const po = (pos ?? []).find((p) => p.id === poId)

  const onPickPO = (id: string) => {
    setPoId(id)
    setDrafts({})
    setResult(null)
  }

  const setDraft = (line: POLineRow, patch: Partial<LineDraft>) => {
    setDrafts((d) => {
      const defaults: LineDraft = {
        po_line_id: line.id,
        quantity_received: 0,
        quantity_accepted: 0,
        quantity_rejected: 0,
        rate_per_unit: Number(line.rate ?? line.rate_per_kg ?? 0),
        uom: line.uom ?? 'NOS',
        gst_pct: 18,
        spare_id: line.spare_id,
        maintenance_work_order_id: line.maintenance_work_order_id,
        direct_charge_to_work_order: line.direct_charge_to_work_order,
      }
      return {
        ...d,
        [line.id]: { ...defaults, ...(d[line.id] ?? {}), ...patch },
      }
    })
  }

  const linesToSubmit: SpareGRNLineInput[] = Object.values(drafts)
    .filter((d) => d.quantity_received > 0)
    .map((d) => ({
      po_line_id: d.po_line_id,
      quantity_received: d.quantity_received,
      quantity_accepted: d.quantity_accepted,
      quantity_rejected: d.quantity_rejected,
      rate_per_unit: d.rate_per_unit,
      uom: d.uom,
      gst_pct: d.gst_pct,
      spare_id: d.spare_id ?? undefined,
      maintenance_work_order_id: d.maintenance_work_order_id ?? undefined,
      direct_charge_to_work_order: d.direct_charge_to_work_order,
    }))

  const submit = useMutation({
    mutationFn: () => {
      if (!poId) throw new Error('Pick a PO first')
      if (linesToSubmit.length === 0) throw new Error('Enter at least one received qty')
      return spareProcurementApi.createSpareGRN({
        po_id: poId,
        lines: linesToSubmit,
        freight_total: freightTotal || undefined,
        remarks: remarks || undefined,
      })
    },
    onSuccess: (r) => {
      setResult(r)
      message.success(`GRN ${r.grn_no} posted — ₹ ${r.total_value.toFixed(2)} landed`)
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'GRN failed'),
  })

  return (
    <div>
      <PageHeader
        title="Spare GRN Entry"
        subtitle="Receive non-cotton items. Stock + WAvg + landed cost are calculated by the backend."
      />

      <Row gutter={12}>
        <Col xs={24} lg={16}>
          <Card title="Pick PO" size="small" style={{ marginBottom: 12 }}>
            <Space>
              <Select
                showSearch
                placeholder="Approved / Sent POs"
                style={{ width: 380 }}
                value={poId}
                onChange={onPickPO}
                options={(pos ?? [])
                  .filter((p) => p.status !== 'DRAFT' && p.status !== 'CANCELLED')
                  .map((p) => ({
                    label: `${p.po_no} — ${p.supplier_name ?? p.supplier_id?.slice(0, 8)}`,
                    value: p.id,
                  }))}
                filterOption={(i, o) =>
                  (o?.label as string).toLowerCase().includes(i.toLowerCase())
                }
              />
              {po && (
                <Tag color={po.status === 'APPROVED' ? 'green'
                  : po.status === 'PARTIALLY_RECEIVED' ? 'orange' : 'default'}>
                  {po.status}
                </Tag>
              )}
            </Space>
          </Card>

          {po && (
            <Card title="PO lines — enter received qty" size="small"
              style={{ marginBottom: 12 }}>
              <Table
                rowKey="id"
                pagination={false}
                size="small"
                dataSource={po.lines}
                columns={[
                  {
                    title: 'Item / Spare', key: 'spare', ellipsis: true,
                    render: (_, line: POLineRow) => (
                      <Space size={4} wrap>
                        <Text strong>{line.spare_id?.slice(0, 8) ?? '—'}</Text>
                        {line.direct_charge_to_work_order
                          && <Tag color="purple">DIRECT CHARGE</Tag>}
                      </Space>
                    ),
                  },
                  {
                    title: 'Ordered', key: 'qty', width: 90,
                    render: (_, line: POLineRow) =>
                      Number(line.quantity ?? line.quantity_kg ?? 0).toFixed(2),
                  },
                  {
                    title: 'UOM', dataIndex: 'uom', key: 'uom', width: 60,
                    render: (v: string | null) => v ?? 'NOS',
                  },
                  {
                    title: 'PO Rate', key: 'rate', width: 110,
                    render: (_, line: POLineRow) =>
                      `₹ ${Number(line.rate ?? line.rate_per_kg ?? 0).toFixed(2)}`,
                  },
                  {
                    title: 'Received',
                    key: 'rcv',
                    width: 100,
                    render: (_, line: POLineRow) => (
                      <InputNumber
                        min={0}
                        size="small"
                        style={{ width: 90 }}
                        value={drafts[line.id]?.quantity_received ?? 0}
                        onChange={(v) => setDraft(line, {
                          quantity_received: Number(v) || 0,
                          quantity_accepted: Number(v) || 0,
                        })}
                      />
                    ),
                  },
                  {
                    title: 'Accepted',
                    key: 'acc',
                    width: 100,
                    render: (_, line: POLineRow) => (
                      <InputNumber
                        min={0}
                        size="small"
                        style={{ width: 90 }}
                        value={drafts[line.id]?.quantity_accepted ?? 0}
                        onChange={(v) => setDraft(line, {
                          quantity_accepted: Number(v) || 0,
                        })}
                      />
                    ),
                  },
                  {
                    title: 'Rejected',
                    key: 'rej',
                    width: 100,
                    render: (_, line: POLineRow) => (
                      <InputNumber
                        min={0}
                        size="small"
                        style={{ width: 90 }}
                        value={drafts[line.id]?.quantity_rejected ?? 0}
                        onChange={(v) => setDraft(line, {
                          quantity_rejected: Number(v) || 0,
                        })}
                      />
                    ),
                  },
                  {
                    title: 'GST %',
                    key: 'gst',
                    width: 90,
                    render: (_, line: POLineRow) => (
                      <InputNumber
                        min={0} max={28} step={0.5}
                        size="small"
                        style={{ width: 80 }}
                        value={drafts[line.id]?.gst_pct ?? 18}
                        onChange={(v) => setDraft(line, {
                          gst_pct: Number(v) || 0,
                        })}
                      />
                    ),
                  },
                ]}
              />

              <Row gutter={12} style={{ marginTop: 12 }}>
                <Col xs={12}>
                  <Form.Item label="Freight / direct cost (₹, optional)">
                    <InputNumber
                      min={0} step={50} style={{ width: '100%' }}
                      value={freightTotal}
                      onChange={(v) => setFreightTotal(Number(v) || 0)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item label="Remarks">
                    <Input
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Button
                type="primary"
                onClick={() => submit.mutate()}
                loading={submit.isPending}>
                Post Spare GRN
              </Button>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Result" size="small">
            {!result && <Text type="secondary">Submit to see the GRN summary.</Text>}
            {result && (
              <Descriptions size="small" bordered column={1}>
                <Descriptions.Item label="GRN No">
                  <Text strong>{result.grn_no}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Lines posted">
                  {result.lines.length}
                </Descriptions.Item>
                <Descriptions.Item label="Total value">
                  <Text strong>₹ {result.total_value.toFixed(2)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <ProcurementStatusBadge status="AWAITING_INVOICE" />
                </Descriptions.Item>
              </Descriptions>
            )}
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="Stock + WAvg + landed rate are computed by the backend. The store officer does not type cost."
            />
            <Form.Item style={{ marginTop: 12 }}>
              <Switch checked disabled checkedChildren="Direct-charge inherited from PO" />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
